import { useEffect } from "react";
import { bool, string, number, object, func } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CommentTypes} from "@repo/utils";
import { renameProjectVersionCommentElementQuery, setProjectVersionCommentReadStatus } from "@query";
import { useEstimateEditorSettings, useEstimateEditorStore, useEstimateComments, useCommentLinker } from "@tools";
import { NavigationSwitch, TooltipIcon } from "@components";
import { Grid } from "@material-ui/core";
import { ChatThread } from "../components/ChatThread";
import { CheckCircle, Link } from "@material-ui/icons";
import useStyle from "./ElementChatPopup.style";
import { useEditorWebsocket } from "@hooks";

export const ElementChatPopupContent = observer(({
  group,
  path,
  taskName,
  useMaxHeight,
  maxDisplayReplies,
  onClose,
}) => {
  const { t } = useTranslation();
  const { allowCommenting } = useEstimateEditorSettings();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const socket = useEditorWebsocket();
  const { isTemplate, isProposal } = useEstimateEditorSettings();
  const { estimateUuid, projectHash } = useParams();
  const classes = useStyle();
  
  const { useInternalComments, useExternalComments } = commentStore;
  const { currentVersionKey } = editorStore;
  const { hasAnyThreads, viewedType, thread } = group;
  
  const {
    linkCopied,
    handleCopyLink,
    handleSetAnchor,
  } = useCommentLinker({
    version: currentVersionKey,
    path,
    viewedType,
    hasThread: Boolean(thread),
  });
  
  useEffect(() => {
    if(thread && !thread.isRead)
      handleRead(group.viewedType);
    return () => {
      handleSetAnchor(false);
    }
  }, []);
  
  useEffect(() => {
    handleTaskNameUpdate();
    handleSetAnchor(true);
  }, [path, viewedType, Boolean(thread)]);
  
  const handleTaskNameUpdate = async () => {
    if(isProposal || !group.isCreated || group.name === taskName || !commentStore.commenterData?.uuid)
      return;
    group.updateName(taskName);
    if(isTemplate)
      socket?.requestCommentPathElementRename(group.path, taskName);
    else
      await renameProjectVersionCommentElementQuery(estimateUuid, commentStore.commenterData?.uuid, currentVersionKey, path, taskName);
  }
  
  const handleRead = async (type) => {
    if(!group[type] || group[type].isRead || isTemplate || !commentStore.commenterData?.uuid)
      return;
    
    group[type].markRead();
    await setProjectVersionCommentReadStatus(group[type].uuid, estimateUuid || projectHash, commentStore.commenterData?.uuid, editorStore.currentVersionKey);
  }
  
  const handleCommentTypeSwitch = async (e, val) => {
    group.setUsedType(val);
    handleRead(val);
  }
  
  if(!allowCommenting && !hasAnyThreads)
    return <></>;
  
  return (
    <>
      {
        !isProposal && (useInternalComments || useExternalComments) && <Grid
          item container
          wrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          className={ classes.header }
        >
          {
            useInternalComments && !useExternalComments &&
            <span className="text-md ml-4 pt-2 font-bold text-secondary">
              {t("views.editor.comments.internal_full")}
            </span>
          }
          {
            useInternalComments && useExternalComments &&
            <>
              <NavigationSwitch
                value={viewedType}
                values={[
                  { value: CommentTypes.INTERNAL, label: t("views.editor.comments.internal"), ariaLabel: "internal comments", className: "nav-internal" },
                  { value: CommentTypes.EXTERNAL, label: t("views.editor.comments.external"), ariaLabel: "external comments", className: "nav-external" },
                ]}
                aria-label="comment type switch"
                onChange={handleCommentTypeSwitch}
                role="navigation"
                line lighter
              />
              <TooltipIcon
                color="primary"
                className="ml-2"
              >
                {t("views.editor.comments.external_info")}
              </TooltipIcon>
            </>
          }
          {
            Boolean(thread) &&
            <TooltipIcon
              size="small"
              color="primary"
              onClick={handleCopyLink}
              className="hoverIcon ml-1"
              icon={linkCopied ? <CheckCircle className="color-success" /> : <Link />}
            >
              {t(`views.editor.comments.link.${linkCopied ? "copied" : "copy"}`)}
            </TooltipIcon>
          }
        </Grid>
      }
      <Grid item container className="p-2">
        <ChatThread
          group={group}
          onClose={onClose}
          useMaxHeight={useMaxHeight}
          maxDisplayReplies={maxDisplayReplies}
        />
      </Grid>
    </>
  );
});

ElementChatPopupContent.propTypes = {
  group: object.isRequired,
  path: string.isRequired,
  taskName: string.isRequired,
  onClose: func.isRequired,
  useMaxHeight: bool,
  maxDisplayReplies: number,
};